import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/Button/Button";
import { CreditsChip } from "@/components/Chip/CreditsChip";
import {
    CreditsIcon,
    GradientDownloadIcon,
    GradientFilesIcon,
    GradientSendIcon,
    SmartMatchIcon,
} from "@/components/Icons/Icons";
import { Modal } from "@/components/Modal/Modal";
import { AppText } from "@/components/Text/AppText";
import { translate } from "@/helper/translate";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useDevice } from "@/hooks/useDevice";
import { getIsModalOpen, getModalProps } from "@/store/modal/selectors";
import { closeModal, openModal } from "@/store/modal/slice";
import { getIsCreditsEnabled } from "@/store/tenant/selectors";
import {
    getUserCreditBalance,
    getUserDocumentLimit,
} from "@/store/user/selectors";
import { ModalTypes } from "@/types/modal";
import { AppRoutes } from "@/types/routes";
import { getIsAJobRelatedPage, getIsResumeBuilderPage } from "@/utils/routes";

type ModalMode = "download" | "buy" | "document-limit" | "smart-match";

interface CreditsSuccessModalProps {
    mode: ModalMode;
    method: "email" | "file";
    jobTitle?: string;
    newResumeId?: string;
}

const CreditsSuccessModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isOpen = useAppSelector(getIsModalOpen(ModalTypes.CREDITS_SUCCESS));
    const modalProps = useAppSelector(
        getModalProps(ModalTypes.CREDITS_SUCCESS),
    );
    const { mode, method, jobTitle, newResumeId }: CreditsSuccessModalProps =
        modalProps ?? {};
    const userCreditBalance = useAppSelector(getUserCreditBalance);
    const documentLimit = useAppSelector(getUserDocumentLimit);
    const { isDesktop } = useDevice();
    const [activeMode, setActiveMode] = useState<ModalMode | undefined>(mode);
    const isJobsPage = useAppSelector(getIsAJobRelatedPage);
    const isCreditsEnabled = useAppSelector(getIsCreditsEnabled);
    const isResumeBuilderPage = useAppSelector(getIsResumeBuilderPage);

    useEffect(() => {
        if (isOpen && mode) {
            setActiveMode(mode);
        }
    }, [isOpen, mode]);

    const handleClose = useCallback(() => {
        dispatch(closeModal(ModalTypes.CREDITS_SUCCESS));
    }, [dispatch]);

    const handleLimitIncrease = useCallback(() => {
        handleClose();
        dispatch(
            openModal({
                modalType: ModalTypes.DOCUMENT_LIMIT,
            }),
        );
    }, [dispatch, handleClose]);

    const navigateAndClose = useCallback(
        (route: AppRoutes | string) => {
            navigate(route);
            dispatch(closeModal(ModalTypes.CREDITS_SUCCESS));
        },
        [dispatch, navigate],
    );

    const onViewResume = useCallback(() => {
        const destination = isDesktop ? "resume" : "preview";
        if (isResumeBuilderPage) {
            navigateAndClose(`/${destination}/${newResumeId!}`);
        } else {
            window.location.href = `/${destination}/${newResumeId!}`;
        }
        dispatch(closeModal(ModalTypes.CREDITS_SUCCESS));
    }, [
        isDesktop,
        isResumeBuilderPage,
        dispatch,
        navigateAndClose,
        newResumeId,
    ]);

    return (
        <Modal
            open={isOpen}
            modalType={ModalTypes.CREDITS_SUCCESS}
            noHeader
            width="2xl"
            gradientBackground
            title=""
            floatingCloseButton
            afterClose={() => setActiveMode(undefined)}
        >
            {activeMode === "download" && (
                <div className="flex flex-col items-center">
                    <div className="mb-2 mt-5">
                        {method === "email" ? (
                            <GradientSendIcon className="h-10 w-10" />
                        ) : (
                            <GradientDownloadIcon className="h-10 w-10" />
                        )}
                    </div>
                    <AppText
                        variant="headings"
                        className="mb-4 w-full text-center text-2xl font-bold text-primary-900"
                        applyGradient
                    >
                        {method === "email"
                            ? "RESUME SENT"
                            : "RESUME DOWNLOADED"}
                    </AppText>
                    <AppText
                        variant="regular"
                        className="text-center text-neutral-900"
                    >
                        {method === "email"
                            ? "Success! Your resume has been sent to your email."
                            : "Success! Your resume has been downloaded as a PDF."}
                    </AppText>
                </div>
            )}

            {activeMode === "buy" && (
                <div className="flex flex-col items-center">
                    <div className="mb-5 mt-5">
                        <CreditsIcon className="h-10 w-10" />
                    </div>
                    <AppText
                        variant="headings"
                        className="mb-4 w-full text-center text-2xl font-bold text-primary-900"
                        applyGradient
                    >
                        PURCHASE SUCCESSFUL
                    </AppText>

                    <AppText
                        variant="regular"
                        className="w-2/3 text-center text-neutral-900"
                    >
                        Credits let you access more features and download your
                        resume as you need them.
                    </AppText>
                </div>
            )}

            {activeMode === "document-limit" && (
                <div className="flex flex-col items-center">
                    <div className="mb-5 mt-5">
                        <GradientFilesIcon className="h-10 w-10" />
                    </div>
                    <AppText
                        variant="headings"
                        className="mb-4 w-full text-center text-2xl font-bold text-primary-900"
                        applyGradient
                    >
                        RESUME LIMIT INCREASED
                    </AppText>

                    <AppText
                        variant="regular"
                        className="w-2/3 text-center text-neutral-900"
                    >
                        Success! You can now create up to {documentLimit}{" "}
                        Resumes.
                    </AppText>
                </div>
            )}

            {activeMode === "smart-match" && (
                <div className="flex flex-col items-center">
                    <div className="mb-2 mt-5">
                        <SmartMatchIcon
                            className="h-20 w-20"
                            gradient
                        />
                    </div>
                    <AppText
                        variant="headings"
                        className="mb-4 w-full text-center text-2xl font-bold text-primary-900"
                        applyGradient
                    >
                        TAILOR RESUME COMPLETE
                    </AppText>
                    <AppText
                        variant="regular"
                        className="w-2/3 text-center text-neutral-900"
                    >
                        Success! Your new resume {translate("optimised")} for
                        the{" "}
                        {jobTitle ? (
                            <>
                                <AppText
                                    variant="regular"
                                    className="!font-medium text-neutral-900"
                                    as="span"
                                >
                                    {jobTitle}
                                </AppText>{" "}
                                role
                            </>
                        ) : (
                            "role"
                        )}{" "}
                        is ready.
                    </AppText>
                </div>
            )}

            {isCreditsEnabled && (
                <div className="mt-6 flex items-center justify-center gap-2">
                    <AppText
                        variant="labelsbuttons"
                        applyGradient
                    >
                        Your new credit balance:
                    </AppText>
                    <CreditsChip
                        credits={userCreditBalance}
                        type="gradient"
                        borderless
                        leftIcon={<CreditsIcon className="h-4 w-4" />}
                    />
                </div>
            )}

            <div className="mt-10 flex items-center justify-center gap-3">
                {activeMode === "download" && (
                    <>
                        <Button
                            variant="solid"
                            onClick={handleClose}
                        >
                            Return to Editor
                        </Button>
                        <Button
                            color="white"
                            variant="solid"
                            className="!text-primary-700"
                            onClick={() =>
                                navigateAndClose(AppRoutes.Dashboard)
                            }
                        >
                            Go to Dashboard
                        </Button>
                    </>
                )}

                {activeMode === "buy" && (
                    <Button
                        variant="solid"
                        onClick={handleClose}
                        className="w-[200px]"
                    >
                        Continue
                    </Button>
                )}

                {activeMode === "document-limit" && (
                    <>
                        <Button
                            variant="solid"
                            onClick={handleClose}
                        >
                            Continue
                        </Button>
                        <Button
                            color="white"
                            variant="solid"
                            className="!text-primary-700"
                            onClick={handleLimitIncrease}
                        >
                            Add More Resumes
                        </Button>
                    </>
                )}

                {activeMode === "smart-match" && (
                    <>
                        {isJobsPage && (
                            <Button
                                color="white"
                                className="!text-primary-700"
                                onClick={() =>
                                    navigateAndClose(AppRoutes.YourJobs)
                                }
                            >
                                Return to Jobs
                            </Button>
                        )}
                        <Button
                            variant="solid"
                            onClick={onViewResume}
                        >
                            View Resume
                        </Button>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default CreditsSuccessModal;
